import { CTABand } from "../../components/site/CTABand";
import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, ArrowLeft, Phone, Check } from "lucide-react";
import { SiteHeader } from "../../components/site/SiteHeader";
import { SiteFooter } from "../../components/site/SiteFooter";
import { FadeIn, Reveal } from "../../components/motion/Reveal";
import { CONTACT } from "../../data/content";
import { STORE_CATEGORIES } from "../../data/storeCategories";

// Brand registry — logo files live in /public/brands/; missing logos render as typographic badges.
const BRAND_NAMES = {
  hikvision: "Hikvision", cpplus: "CP Plus", dahua: "Dahua", uniview: "Uniview", hanwha: "Hanwha",
  cisco: "Cisco", dlink: "D-Link", tplink: "TP-Link", ubiquiti: "Ubiquiti", mikrotik: "MikroTik", netgear: "Netgear",
  belden: "Belden", molex: "Molex", panduit: "Panduit", legrand: "Legrand",
  zkteco: "ZKTeco", essl: "eSSL", matrix: "Matrix", honeywell: "Honeywell", bosch: "Bosch", siemens: "Siemens",
  panasonic: "Panasonic", grandstream: "Grandstream", yealink: "Yealink", poly: "Poly",
  ravel: "Ravel", ahuja: "Ahuja", jbl: "JBL", yamaha: "Yamaha",
  hp: "HP", dell: "Dell", lenovo: "Lenovo", apc: "APC", eaton: "Eaton", seagate: "Seagate", westerndigital: "Western Digital", synology: "Synology",
  microsoft: "Microsoft", kaspersky: "Kaspersky", fortinet: "Fortinet", sophos: "Sophos", veeam: "Veeam",
};

const BrandChip = ({ slug, file }) => {
  const name = BRAND_NAMES[slug] || slug;
  return (
    <div className="flex items-center justify-center h-[4.5rem] px-5 bg-white/[0.94] border border-white/10 transition-transform duration-300 hover:-translate-y-0.5" title={name} data-testid={`brand-${slug}`}>
      {file ? (
        <img src={`/brands/${file}`} alt={`${name} — brand supplied and installed by NexSecure Technologies`} loading="lazy" className="max-h-10 max-w-[130px] object-contain" />
      ) : (
        <span className="text-nx-navy font-semibold text-sm tracking-[0.12em] uppercase whitespace-nowrap">{name}</span>
      )}
    </div>
  );
};

export default function StoreCategory() {
  const { slug } = useParams();
  const [brandFiles, setBrandFiles] = useState({});
  useEffect(() => {
    axios.get("/brands/brands.json").then((r) => setBrandFiles(r.data)).catch(() => {});
  }, []);
  const idx = STORE_CATEGORIES.findIndex((c) => c.slug === slug);
  if (idx === -1) return <Navigate to="/store" replace />;
  const cat = STORE_CATEGORIES[idx];
  const next = STORE_CATEGORIES[(idx + 1) % STORE_CATEGORIES.length];

  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased" data-testid="store-category-page">
      <SiteHeader />

      {/* Hero with category photography */}
      <section className="relative overflow-hidden pt-20 sm:pt-24 lg:pt-36 pb-16 sm:pb-20">
        <div className="absolute inset-0">
          <img src={`/catalog/${cat.image}.webp`} alt={`${cat.name} — supplied and installed by NexSecure Technologies, Kerala`} className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-nx-navy/70 via-nx-navy/85 to-nx-navy" />
        </div>
        <div className="relative nx-wrap">
          <FadeIn delay={0.1}>
            <Link to="/store" className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-slate-400 hover:text-nx-goldlight transition-colors mb-8" data-testid="category-back-link">
              <ArrowLeft size={13} /> All Products &amp; Solutions
            </Link>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div className="flex items-center gap-4 mb-5">
              <span className="h-px w-12 bg-nx-gold/70" />
              <span className="text-[11px] sm:text-xs tracking-[0.3em] uppercase text-nx-goldlight">{cat.tagline}</span>
            </div>
          </FadeIn>
          <h1 className="font-marcellus text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.06] max-w-4xl" data-testid="category-title">{cat.name}</h1>
          <FadeIn delay={0.35}>
            <p className="mt-6 text-base md:text-lg text-slate-400 max-w-2xl leading-relaxed" data-testid="category-intro">{cat.intro}</p>
          </FadeIn>
          <FadeIn delay={0.5}>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to={`/request-a-quote?product=${encodeURIComponent(cat.name)}`} className="inline-flex items-center gap-2 bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-7 py-4 transition-all duration-300 hover:-translate-y-0.5" data-testid="category-quote-cta">
                Contact Sales <ArrowRight size={15} />
              </Link>
              <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-2 border border-white/20 hover:border-nx-gold/60 text-slate-200 text-xs font-semibold tracking-[0.14em] uppercase px-7 py-4 transition-colors" data-testid="category-call-cta">
                <Phone size={14} /> {CONTACT.phone}
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Product groups */}
      <section className="py-14 sm:py-20 bg-nx-navy2 border-y border-white/10">
        <div className="nx-wrap">
          <div className="grid md:grid-cols-2 gap-6">
            {cat.groups.map((g) => (
              <Reveal key={g.title}>
                <div className="border border-white/10 bg-white/[0.02] p-7 h-full" data-testid={`category-group-${g.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <h2 className="font-marcellus text-xl text-white mb-5 flex items-center gap-3">
                    <span className="h-px w-6 bg-nx-gold/70" />{g.title}
                  </h2>
                  <ul className="grid sm:grid-cols-2 gap-x-5 gap-y-2.5">
                    {g.items.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-sm text-slate-300 leading-relaxed">
                        <Check size={14} className="text-nx-goldlight shrink-0 mt-0.5" />{item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>

          {cat.brands?.length > 0 && (
            <Reveal>
              <div className="mt-10" data-testid="category-brands">
                <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500 mb-4">Brands we supply &amp; install</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {cat.brands.map((b) => <BrandChip key={b} slug={b} file={brandFiles[b]?.file} />)}
                </div>
                <p className="text-[11px] text-slate-600 mt-4">Genuine products with manufacturer warranty. Don't see your preferred brand? Ask us — we source across OEMs.</p>
              </div>
            </Reveal>
          )}

          {cat.applications?.length > 0 && (
            <Reveal>
              <div className="mt-10 border border-nx-gold/20 bg-nx-gold/[0.04] p-7" data-testid="category-applications">
                <p className="text-[10px] tracking-[0.24em] uppercase text-nx-goldlight mb-4">Where we deploy this</p>
                <div className="flex flex-wrap gap-2.5">
                  {cat.applications.map((a) => (
                    <span key={a} className="text-xs text-slate-300 border border-white/15 px-3.5 py-2">{a}</span>
                  ))}
                </div>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* CTA + next category */}
      <section className="py-14 sm:py-20">
        <div className="nx-wrap grid lg:grid-cols-2 gap-6">
          <Reveal>
            <div className="border border-nx-gold/30 bg-nx-gold/[0.05] p-8 h-full flex flex-col justify-between" data-testid="category-cta-panel">
              <div>
                <p className="text-[10px] tracking-[0.28em] uppercase text-nx-goldlight mb-3">Talk to an engineer</p>
                <p className="font-marcellus text-2xl text-white leading-snug">Tell us your site and requirement — we respond with a detailed, itemised proposal.</p>
              </div>
              <Link to={`/request-a-quote?product=${encodeURIComponent(cat.name)}`} className="mt-6 inline-flex w-fit items-center gap-2 bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-7 py-4 transition-colors" data-testid="category-bottom-cta">
                Request a Quote <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to={`/store/${next.slug}`} className="group border border-white/10 hover:border-nx-gold/40 bg-white/[0.02] p-8 h-full flex flex-col justify-between transition-colors" data-testid="category-next-link">
              <div className="relative overflow-hidden h-40 mb-6">
                <img src={`/catalog/${next.image}.webp`} alt={`${next.name} — NexSecure Technologies`} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
                <div className="absolute inset-0 bg-gradient-to-t from-nx-navy/80 to-transparent" />
              </div>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500 mb-2">Next category</p>
                  <p className="font-marcellus text-xl text-white">{next.name}</p>
                </div>
                <ArrowRight size={20} className="text-nx-goldlight transition-transform duration-300 group-hover:translate-x-1.5" />
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      <CTABand />
      <SiteFooter />
    </div>
  );
}
